import { Component, EventEmitter, Inject, InjectionToken, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, ReplaySubject, Subject, combineLatest, delay, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs';
import { QQQuestionViewModel } from 'src/app/models/qq-question.view-model';
import { QQQuestionnairePageViewModel } from 'src/app/models/qq-questionnaire-page.view-model';
import { QQQuestionnaireViewModel } from 'src/app/models/qq-questionnaire.view-model';
import { QuoteModel } from 'src/app/models/quote.model';
import { logError } from 'src/app/sentry';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { QuestionService } from 'src/app/services/question.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { SharedStateService } from 'src/app/services/shared-state.service';
import { StorageService } from 'src/app/services/storage.service';
import { Loader } from 'src/app/util/loader';
import { SITE_CONTENT_CLICK, SiteContentClickEvent } from '../shared/site-content/site-content.component';
import { BeatYourBrokerHelper } from './apg-beat-your-broker-helper';

export const QUICK_QUOTE = new InjectionToken<BehaviorSubject<QQQuestionnaireViewModel>>("QUICK_QUOTE$");

@Component({
  selector: 'app-apg-beat-your-broker',
  templateUrl: './apg-beat-your-broker.component.html',
  styleUrls: ['./apg-beat-your-broker.component.scss'],
  providers: [
    { provide: SITE_CONTENT_CLICK, useFactory: () => new Subject<SiteContentClickEvent>() },
    { provide: QUICK_QUOTE, useFactory: () => new BehaviorSubject<QQQuestionnaireViewModel>(null) },
    { provide: Loader, useValue: new Loader() }
  ]
})
export class ApgBeatYourBrokerComponent implements OnInit, OnDestroy {

  @Input() name: string;
  @ViewChild(NgForm) form: NgForm;
  @Output() displayMode = new EventEmitter<string>();

  refresh$ = new ReplaySubject<boolean>(1);
  disposed$ = new Subject();
  mode: 'coverchecker' | 'coverCalculator' = 'coverchecker';
  error: string;
  quote: QuoteModel;
  otpContentKey: 'BeatYourBrokerOtp';

  constructor(
    private sharedStateService: SharedStateService<QQQuestionnaireViewModel>,
    private questionnaireService: QuestionnaireService,
    private helper: BeatYourBrokerHelper,
    private questionService: QuestionService,
    private analyticsService: AnalyticsService,
    @Inject(SITE_CONTENT_CLICK) private siteContentClick: Subject<SiteContentClickEvent>,
    @Inject(QUICK_QUOTE) public quickquote$: BehaviorSubject<QQQuestionnaireViewModel>,
    public loader: Loader,
    private router: Router,
    private storageService: StorageService
  ) {
  }

  ngOnInit() {
    combineLatest([
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      ),
      this.helper.result$,
      this.quickquote$
    ]).pipe(
      takeUntil(this.disposed$),
      map(([nav, result, quote]) => {
        const [_, path, page] = nav.url.split('/');
        const pageNumber = parseInt(page);
        if (path !== 'coverchecker')
          return 'narrow';

        const qp = quote.getPage(pageNumber);
        if (qp.type === "BeatYourBrokerResult")
          return result.cssClass;
        else
          return 'narrow';
      }),
      startWith('narrow')
    ).subscribe(this.displayMode);

    this.helper.allowPage(0);
    this.siteContentClick.subscribe(x => {
      const target = x.event.currentTarget as HTMLAnchorElement;
      if (target.href === 'https://app/cover-calculator') {
        // we need a delay before changing mode so that the help popup can close
        setTimeout(() => this.mode = 'coverCalculator', 500);
        x.event.preventDefault();
      }
    });
    this.router.initialNavigation();
  }

  ngOnDestroy(): void {
    this.disposed$.next(null);
  }

  ngAfterViewInit() {
    this.refresh$.pipe(
      switchMap(x => this.loadViewModel())
    ).subscribe(result => {
      this.quickquote$.next(result);
      this.sharedStateService.updateState(result);
    });

    this.refresh$.next(true);
  }

  validatePage(question: QQQuestionViewModel, answer: string, page: QQQuestionnairePageViewModel) {
    const linked = question.options?.linked;

    if (linked) {
      question.options.linked.forEach((value: any) => page.questions
        .filter(x => x.key == value)
        .forEach(lq => {
          if (lq.answer && lq.answer.trim() !== "")
            lq.setAnswer(lq.answer, true);
          if (lq.options.calculate) {
            this.questionService.getCalculatedOptions(lq);
          }
        }));
    }
  }

  async loadViewModel() {
    try {
      this.loader.loading = true;
      const saved = this.storageService.getAnswers(this.name);
      const model = await this.questionnaireService.getQuestionnaireViewModel(this.name, true, saved?.answers, saved?.key);
      model.sourceUrl = this.analyticsService.getFullPath('beat_your_broker');
      
      model.pages.forEach(p => p.onQuestionAnswered.subscribe(x => this.validatePage(x.question, x.answer, p)));
      model.answerSaved$.pipe(
        takeUntil(this.disposed$),
        delay(1),
        // delay by 1 ms so that the api call to save the quote has a chance to start and set the saving promise on model
        // so that we can get the quote key if it's a new quote
        tap(async x => {
          if (model.saving)
            await model.saving;
          this.storageService.saveAnswers(model.name, { key: model.quoteKey, answers: model.answersObject })
        })
      ).subscribe();

      this.analyticsService
        .mapTrackingData()?.map(x => model.saveAnswer(x.key, x.value)) ?? [];

      return model;
    } catch (e) {
      logError(`Error loading ${this.name} questionnaire`, e);
      this.error = `Sorry, there was an error loading the ${this.name} questionnaire`;
    } finally {
      this.loader.loading = false;
    }
  }

}
